import isNil from 'lodash/isNil'
import { Map, List } from 'immutable'
import { USD } from 'services/currency'
import { selectAuthorized } from 'services/auth/selectors'
import { selectResolverSKUParam } from 'services/resolver/selectors'
import { decodeGiftUrlSku, findPlanInDataList } from 'services/plans'
import { selectPlans } from 'services/plans/selectors'
import { getPrimary } from 'services/languages'
import _get from 'lodash/get'
import { get as getConfig } from 'config'
import { EN } from 'services/languages/constants'
import { getToday } from 'services/date-time/getToday'
import 'store/types'
import { GIFT_READY_STATE } from 'services/gift'

/**
 * @param {ReduxState} state redux state
 * @returns {Map}
 */
export function selectGift (state) {
  return state.gift || Map()
}

/**
 * @param {ReduxState} state redux state
 * @returns {Map}
 */
export function selectGiftPlan (state) {
  const sku = selectGiftSku(state)
  const plansStore = selectPlans(state)
  const isPlansLoaded = !plansStore.get('processing', true)
    && plansStore.getIn(['data', 'plans'], List()).size > 0
  if (isPlansLoaded && isValidSku(sku)) {
    const selectedPlan = findPlanInDataList(plansStore, sku)
    if (!isNil(selectedPlan)) {
      return selectedPlan
    }
  }
  return Map()
}

/**
 * @param {ReduxState} state redux state
 * @returns {Bool}
 */
export function selectGiftOrderProcessing (state) {
  const giftStore = selectGift(state)
  return giftStore.getIn(['orderData', 'processing'], false)
}

/**
 * @param {ReduxState} state redux state
 * @returns {String}
 */
export function selectGiftOrderError (state) {
  const giftStore = selectGift(state)
  return giftStore.getIn(['orderError', 'error'], false)
}

/**
 * @param {ReduxState} state redux state
 * @returns {String}
 */
export function selectGiftOrderErrorCode (state) {
  const giftStore = selectGift(state)
  return giftStore.getIn(['orderError', 'errorCode'], null)
}

/**
 * @param {ReduxState} state redux state
 * @returns {Bool}
 */
export function selectGiftOrderSuccess (state) {
  const giftStore = selectGift(state)
  return giftStore.getIn(['orderData', 'success'], false)
}

/**
 * @param {ReduxState} state redux state
 * @returns {String}
 */
export function selectGiftToken (state) {
  const giftStore = selectGift(state)
  return giftStore.getIn(['orderData', 'gift', 'token'], '')
}

function isValidSku (sku) {
  return typeof sku === 'string' && sku.length > 0
}

export function selectGiftSku (state) {
  const giftStore = selectGift(state)
  const resolverSku = selectResolverSKUParam(state)
  const sku = giftStore.getIn(['details', 'planSku'], '')
    || decodeGiftUrlSku(resolverSku)
  return (isValidSku(sku) && sku) || ''
}

/**
 * @param {ReduxState} state redux state
 * @returns {import('immutable').Map}
 */
export function selectGiftDetails (state) {
  const giftState = selectGift(state)
  return giftState.get('details') || Map()
}

/**
 * @param {ReduxState} state redux state
 * @returns {import('immutable').Map}
 */
export function selectGiftRedeemData (state) {
  const giftState = selectGift(state)
  return giftState.get('redeemData') || Map()
}

/**
 * @param {ReduxState} state redux state
 * @returns {import('immutable').Map}
 */
export function selectGiftRedeemErrorData (state) {
  const giftState = selectGift(state)
  return giftState.get('redeemError') || Map()
}

/**
 * @param {ReduxState} state redux state
 * @returns {String}
 */
export function selectRecipientEmail (state) {
  const giftState = selectGift(state)
  const recipientEmail = giftState.getIn(['details', 'recipientEmail'], '')
  return recipientEmail
}

/**
 * @param {ReduxState} state redux state
 * @returns {String}
 */
export function selectTokenURL (state) {
  const resolver = state.resolver || Map()
  return resolver.getIn(['query', 'token'], '')
}

/**
 * Returns the specified date, if it exists, or today's date otherwise
 * @param {ReduxState} state redux state
 * @returns {String | Date}
 */
export function selectDeliveryDate (state) {
  const gift = selectGift(state)
  const deliveryDate = gift.getIn(['details', 'deliveryDate'])
  if (!isNil(deliveryDate)) {
    return deliveryDate
  }
  return getToday()
}

/**
 * Returns bool if get request 404s
 * @param {ReduxState} state redux state
 * @returns {Boolean}
 */
export function selectGiftTokenError (state) {
  const gift = selectGift(state)
  const statusCode = gift.getIn(['details', 'statusCode'])
  if (statusCode === 404) {
    return true
  }
  return false
}

/**
 * Determines if a user can purchase a gift based on login and payment currency
 * @param {ReduxState} state redux state
 * @returns {String}
 */
export function selectUserCanPurchaseGift (state) {
  const { userAccount = Map() } = state
  const isLoggedIn = selectAuthorized(state)
  const processingComplete = userAccount.get('billingSubscriptionsProcessing') === false

  if (!processingComplete) {
    return GIFT_READY_STATE.NOT_READY
  }

  const config = getConfig()
  const isGiftFeatureEnabled = !!_get(config, ['features', 'gift', 'checkout'])
  const subscription = userAccount.getIn(['details', 'data', 'billing', 'subscriptions'], Map())
  const accountId = subscription.get('billingAccountId')
  const currency = subscription.get('currencyIso', USD)
  if (isGiftFeatureEnabled && isLoggedIn && processingComplete) {
    if (accountId) {
      return currency === USD
        ? GIFT_READY_STATE.CAN_PURCHASE
        : GIFT_READY_STATE.CANNOT_PURCHASE
    }
    return GIFT_READY_STATE.CAN_PURCHASE
  }
  return GIFT_READY_STATE.CANNOT_PURCHASE
}

/**
 * Determines if a user is gift compatible
 * @param {ReduxState} state redux state
 * @returns {Boolean}
 */
export function selectIsGiftCompatible (state) {
  const config = getConfig()
  const userCanPurchaseGift = selectUserCanPurchaseGift(state)
  const isUserLanguageEn = getPrimary(state.user.getIn(['data', 'language'], List())) === EN
  const isGiftIconCTAEnabled = !!_get(config, ['features', 'gift', 'iconCTA'])

  if (userCanPurchaseGift === GIFT_READY_STATE.NOT_READY) {
    return GIFT_READY_STATE.NOT_READY
  }
  if (userCanPurchaseGift === GIFT_READY_STATE.CAN_PURCHASE
    && isUserLanguageEn && isGiftIconCTAEnabled) {
    return GIFT_READY_STATE.CAN_PURCHASE
  }
  return GIFT_READY_STATE.CANNOT_PURCHASE
}

/**
 * @param {ReduxState} state redux state
 * @returns {Bool}
 */
export function selectGiftRedeemProcessing (state) {
  const giftStore = selectGift(state)
  return giftStore.getIn(['redeemData', 'processing'], false)
}

/**
 * @param {ReduxState} state redux state
 * @returns {Bool}
 */
export function selectGiftRedeemErrorState (state) {
  const giftStore = selectGift(state)
  return giftStore.has('redeemData') && !giftStore.getIn(['redeemData', 'success'], undefined)
}

/**
 * @param {ReduxState} state redux state
 * @returns {Bool}
 */
export function selectGiftRedeemSuccess (state) {
  const giftStore = selectGift(state)
  return !!giftStore.getIn(['redeemData', 'success'], undefined)
}

/**
 * @param {ReduxState} state redux state
 * @returns {Bool}
 */
export function selectGiftIsExpired (state) {
  const giftStore = selectGift(state)
  const expiresAt = giftStore.getIn(['details', 'expiresAt'], undefined)
  const now = new Date()
  now.setHours(0, 0, 0, 0)

  return expiresAt && (Date.parse(expiresAt) <= now)
}
